"use client";
import { Button } from "@cmp-src/primitives/button";
import { cn } from "@gettactic/helpers";
import pluralize from "pluralize";
import React from "react";
import { useReserveForm } from "@/features/reserve/components/Reserve/ReserveFormProvider";
import { AddAnotherRoomButton } from "@/features/reserve/components/RoomSelection/AddAnotherRoomButton";

export interface MapReserveCardActionsProps {
  onSubmit: (event: React.BaseSyntheticEvent) => void;
  /** Whether the card may offer picking more rooms for this reservation. */
  canSelectMoreRooms: boolean;
  /** Rooms already held by the reservation, including the one being shown. */
  roomsCount: number;
  /** Runs just before the map is armed to pick one more room. */
  onStartPicking?: () => void;
  className?: string;
}

const classes = {
  Row: "flex w-full items-center gap-2",
  // Always stretches: on its own it fills the footer the way it did before the
  // "add another room" button existed, and next to that button it takes the rest.
  SubmitButton: "flex-1"
};

export const MapReserveCardActions: React.FC<MapReserveCardActionsProps> = ({
  onSubmit,
  canSelectMoreRooms,
  roomsCount,
  onStartPicking,
  className
}) => {
  const { schedule, organizerRequiresDelegatedAccess } = useReserveForm();

  const submitLabel = React.useMemo(() => {
    if (schedule) {
      return "Edit Reservation";
    }
    if (!canSelectMoreRooms) {
      return "Reserve";
    }
    return roomsCount > 1
      ? `Reserve ${roomsCount} ${pluralize("Room", roomsCount)}`
      : "Reserve Room";
  }, [schedule, canSelectMoreRooms, roomsCount]);

  return (
    <div className={cn(classes.Row, className)}>
      <Button
        type="submit"
        onClick={onSubmit}
        className={classes.SubmitButton}
        disabled={organizerRequiresDelegatedAccess}
      >
        {submitLabel}
      </Button>
      {canSelectMoreRooms ? (
        <AddAnotherRoomButton onStartPicking={onStartPicking} />
      ) : null}
    </div>
  );
};
